import { useState } from "react";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, parseJSON } from "utils/datetime";

import Box from "components/Box";
import PageHeader from "components/PageHeader";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";

import ActionButtons from "entities/TableStorage/components/ActionButtons";
import HeaderSubTitle from "entities/TableStorage/components/HeaderSubTitle";
import Table from "entities/TableStorage/components/Table";
import TableImportUpdateDialog from "entities/TableStorage/components/TableImportUpdateDialog";
import TableSkeleton from "entities/TableStorage/components/TableSkeleton";
import { ITableWithImportTable } from "entities/TableStorage/sdk";

interface IOtherMiscTableDetails {
  dealId: number;
  table: ITableWithImportTable;
  mutateTables: () => void;
}

const OtherMiscTableDetails = ({
  dealId,
  table,
  mutateTables,
}: IOtherMiscTableDetails) => {
  const [showImportUpdateDialog, setShowImportUpdateDialog] = useState(false);

  return (
    <Stack>
      <Stack
        spacing={2}
        sx={{ alignSelf: "center", width: "100%" }}
        data-testid="table-import-entity"
      >
        <PageHeader
          title={
            _.isUndefined(table) ? (
              <Stack
                direction="row"
                spacing={theme.spacing(1)}
                alignItems="center"
              >
                <Skeleton height={55} width={250} />
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={theme.spacing(1)}
                alignItems="center"
              >
                <Text variant="h2" data-testid="page-header-name">
                  {table.name}
                </Text>
              </Stack>
            )
          }
          subTitle={<HeaderSubTitle table={table} />}
          actions={
            _.isUndefined(table) ? (
              <Skeleton height={60} width={50} />
            ) : (
              <ActionButtons
                deleteCallback={mutateTables}
                tableId={table.id}
                initialValue={table.name}
                dealId={dealId}
                updateCallback={mutateTables}
                onImportUpdate={() => setShowImportUpdateDialog(true)}
              />
            )
          }
          backgroundColor={colors.blue40}
        />
        <Stack>
          <Box>
            {_.isUndefined(table) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton height={63} width={150} />
                <Skeleton height={63} width={150} />
              </Stack>
            )}
          </Box>
          {_.isUndefined(table.rows) ? (
            <TableSkeleton />
          ) : (
            <Table tableData={table.rows} />
          )}
        </Stack>

        <Text
          variant="text2"
          sx={{
            color: colors.gray80,
            paddingBottom: 4,
          }}
          data-testid="table-timestamp"
        >
          Table created on{" "}
          {format(parseJSON(table.created_at), FORMATS.FRONTEND.DATE)} by{" "}
          {table.created_by.first_name} {table.created_by.last_name}
          {!_.isNil(table.updated_by) &&
            ` • Last
          edited on ${format(parseJSON(table.updated_at), FORMATS.FRONTEND.DATE)} by ${table.updated_by.first_name} ${table.updated_by.last_name}`}
        </Text>
      </Stack>

      <TableImportUpdateDialog
        dealId={dealId}
        updateCallback={() => mutateTables()}
        show={showImportUpdateDialog}
        onClose={() => setShowImportUpdateDialog(false)}
        tableImportId={table.deal_company_import_table.id}
        initialValues={{
          sheet_name: table.deal_company_import_table.sheet_name,
          column_start: table.deal_company_import_table.column_start,
          column_end: table.deal_company_import_table.column_end,
          row_start: table.deal_company_import_table.row_start,
          row_end: table.deal_company_import_table.row_end,
        }}
      />
    </Stack>
  );
};

export default OtherMiscTableDetails;
